import type { ReactNode } from 'react';
import { GridListItem } from 'react-aria-components';
import {
  SelectionCheck,
  SelectionCircle
} from 'v5/design-system/SelectionCheck';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { CollectionDragHandle } from 'v5/patterns/dnd/CollectionDragHandle';
import { cn } from 'v5/platform/dom/cn';
import type { TileState } from './TileCollection';

type DataAttributes = { [name: `data-${string}`]: string | undefined };

/**
 * The one look every row of a list layout shares, around a body of the host's: a line under it,
 * a tint on hover, the checklist's grey picked, the black ring for the keyboard's focus only; rows
 * not picked keep their full contrast. At its start the handle (when it
 * drags) and the circle that picks, the tiles' own `SelectionCheck`, in line.
 */
export function RowShell({
  tile,
  selectLabel,
  deselectLabel = selectLabel,
  dragLabel,
  current = false,
  locked,
  className,
  children,
  ...data
}: {
  tile: TileState;
  /** The circle's name. */
  selectLabel: string;
  /** Its name while picked; `selectLabel` without it. */
  deselectLabel?: string;
  /** The handle's name; the row shows it when it drags. */
  dragLabel: string;
  /** The one of its set shown elsewhere (open beside the list): a grey tint. */
  current?: boolean;
  /**
   * Picked for good (already where the picks go): the grey ticked circle, named by this (on hover
   * and to a screen reader). The host keeps the row from being picked (`pickable`).
   */
  locked?: string;
  /** The row's own box, e.g. its height in a plain list. */
  className?: string;
  children: ReactNode | ((state: { isSelected: boolean }) => ReactNode);
} & DataAttributes) {
  return (
    <GridListItem
      id={tile.key}
      textValue={tile.textValue}
      onAction={tile.onAction}
      // what the selection rectangle picks
      data-item-id={tile.marquee ? tile.key : undefined}
      data-current={current || undefined}
      style={tile.style}
      className={cn(
        'group/row relative flex size-full min-w-0 cursor-default items-center gap-3 border-b border-border-default px-3 outline-none transition-colors',
        'data-hovered:bg-bg-subtle/60 data-current:bg-bg-subtle',
        // picked rows in a run read as one block: the checklist's grey, no ring and no line between them
        'data-selected:border-transparent data-selected:bg-bg-subtle data-selected:data-hovered:bg-bg-subtle-2',
        // the keyboard's ring, inside the row: the rows touch
        'data-focus-visible:ring-2 data-focus-visible:ring-bg-emphasis data-focus-visible:ring-inset',
        tile.picking && 'cursor-pointer',
        className
      )}
      {...data}
    >
      {({ isSelected }) => (
        <>
          {tile.draggable && (
            <CollectionDragHandle
              label={dragLabel}
              className="-mx-1.5 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100"
            />
          )}
          {locked !== undefined ? (
            <SimpleTooltip title={locked}>
              <span
                role="img"
                aria-label={locked}
                className="shrink-0 rounded-full"
              >
                <SelectionCircle locked />
              </span>
            </SimpleTooltip>
          ) : (
            tile.selectable && (
              <SelectionCheck
                label={isSelected ? deselectLabel : selectLabel}
                excludeFromTabOrder
              />
            )
          )}
          <div
            // React Aria keeps `aria-current` off its row: the row's content carries it
            aria-current={current || undefined}
            // white chips and avatars take the row's grey (hovered, picked) instead of patching it;
            // in the dark theme multiply would sink them, so they stay as they are
            className="flex min-w-0 flex-1 items-center gap-3 self-stretch mix-blend-multiply dark:mix-blend-normal"
          >
            {typeof children === 'function'
              ? children({ isSelected })
              : children}
          </div>
          {tile.dropLine}
        </>
      )}
    </GridListItem>
  );
}
